import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { checkProjectExists, registerInvitedUser } from '../../Services/InviteService';

interface FormData {
    email: string;
    invitationCode: string;
    firstName: string;
    lastName: string;
    password: string;
    confirmPassword: string;
}

interface Errors {
    email?: string;
    invitationCode?: string;
    firstName?: string;
    lastName?: string;
    password?: string;
    confirmPassword?: string;
    general?: string;
}

const InviteRegistration: React.FC = () => {
    const { ownerId } = useParams<{ ownerId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();

    // Wizard state
    const [currentSlide, setCurrentSlide] = useState(0); // 0: welcome, 1: email, 2: code, 3: password

    // Form state
    const [formData, setFormData] = useState<FormData>({
        email: '',
        invitationCode: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
    });

    const [errors, setErrors] = useState<Errors>({});
    const [loading, setLoading] = useState(false);

    // MFA-style code inputs
    const [codeDigits, setCodeDigits] = useState<string[]>(['', '', '', '', '', '']);
    const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Load query params on mount
    useEffect(() => {
        const emailParam = searchParams.get('email');
        const codeParam = searchParams.get('code');

        if (emailParam || codeParam) {
            setFormData((prev) => ({
                ...prev,
                email: emailParam || prev.email,
                invitationCode: codeParam || prev.invitationCode,
            }));
            // Also populate code digits if code param exists
            if (codeParam && codeParam.length === 6) {
                setCodeDigits(codeParam.split(''));
            }
        }
    }, [searchParams]);

    // Handle MFA code digit change
    const handleCodeDigitChange = (index: number, value: string): void => {
        // Only allow single digit
        const digit = value.replace(/\D/g, '').slice(-1);

        const newDigits = [...codeDigits];
        newDigits[index] = digit;
        setCodeDigits(newDigits);

        // Update formData with combined code
        const combinedCode = newDigits.join('');
        setFormData((prev) => ({
            ...prev,
            invitationCode: combinedCode,
        }));

        // Clear error
        if (errors.invitationCode) {
            setErrors((prev) => ({ ...prev, invitationCode: undefined }));
        }

        // Auto-focus next input
        if (digit && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace in MFA code
    const handleCodeKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste in MFA code
    const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pastedData) {
            const newDigits = pastedData.split('').concat(['', '', '', '', '', '']).slice(0, 6);
            setCodeDigits(newDigits);
            setFormData((prev) => ({
                ...prev,
                invitationCode: newDigits.join(''),
            }));
            // Focus last filled input or first empty
            const lastIndex = Math.min(pastedData.length - 1, 5);
            codeInputRefs.current[lastIndex]?.focus();
        }
    };

    // Handle input changes
    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error for this field
        if (errors[name as keyof Errors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    // Validate email
    const validateEmail = (): boolean => {
        const newErrors: Errors = {};
        if (!formData.email) {
            newErrors.email = 'Por favor, introduce tu email';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'El formato de email no es válido';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate invitation code
    const validateCode = (): boolean => {
        const newErrors: Errors = {};
        if (!formData.invitationCode) {
            newErrors.invitationCode = 'Por favor, introduce el código de invitación';
        } else if (!/^\d{6}$/.test(formData.invitationCode)) {
            newErrors.invitationCode = 'El código de invitación debe tener 6 dígitos numéricos';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate password form
    const validatePasswordForm = (): boolean => {
        const newErrors: Errors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'El nombre es obligatorio';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Los apellidos son obligatorios';
        }
        if (!formData.password) {
            newErrors.password = 'La contraseña es obligatoria';
        } else if (formData.password.length < 8) {
            newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        } else if (!/[A-Z]/.test(formData.password)) {
            newErrors.password = 'La contraseña debe tener al menos 1 letra mayúscula';
        } else if (!/[a-z]/.test(formData.password)) {
            newErrors.password = 'La contraseña debe tener al menos 1 letra minúscula';
        } else if (!/[0-9]/.test(formData.password)) {
            newErrors.password = 'La contraseña debe tener al menos 1 número';
        }
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirma tu contraseña';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Check if project exists (API call)
    const verifyInvitationCode = async (): Promise<boolean> => {
        if (!ownerId) {
            setErrors({ general: 'URL inválida: falta el identificador del propietario' });
            return false;
        }

        try {
            setLoading(true);
            const response = await checkProjectExists(
                formData.email,
                parseInt(formData.invitationCode, 10),
                ownerId
            );
            if (!response.exists) {
                setErrors({ invitationCode: 'El código de invitación no es válido o ha expirado' });
                return false;
            }
            return true;
        } catch (error: any) {
            console.error('Error checking invitation code:', error);
            let errorMessage = 'Error al verificar el código de invitación';
            const backendError = error.response?.data?.error;
            if (backendError?.includes('6-digit number')) {
                errorMessage = 'El código de invitación debe tener 6 dígitos numéricos';
            } else if (backendError) {
                errorMessage = backendError;
            }
            setErrors({ invitationCode: errorMessage });
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Register user and auto-login
    const handleRegister = async (): Promise<void> => {
        if (!ownerId) {
            setErrors({ general: 'URL inválida: falta el identificador del propietario' });
            return;
        }

        try {
            setLoading(true);
            setErrors({});

            // Register the user
            await registerInvitedUser(
                formData.firstName.trim(),
                formData.lastName.trim(),
                formData.email,
                formData.password,
                parseInt(formData.invitationCode, 10),
                ownerId
            );

            // Auto-login after successful registration
            await login(formData.email, formData.password, () => {
                navigate('/user/members');
            });
        } catch (error: any) {
            console.error('Error registering user:', error);
            let errorMessage = 'Error al crear la cuenta. Inténtalo de nuevo.';
            const backendError = error.response?.data?.error;
            if (backendError) {
                if (backendError.includes('uppercase')) {
                    errorMessage = 'La contraseña debe tener al menos 1 letra mayúscula';
                } else if (backendError.includes('lowercase')) {
                    errorMessage = 'La contraseña debe tener al menos 1 letra minúscula';
                } else if (backendError.includes('number')) {
                    errorMessage = 'La contraseña debe tener al menos 1 número';
                } else if (backendError.includes('8 characters')) {
                    errorMessage = 'La contraseña debe tener al menos 8 caracteres';
                } else {
                    errorMessage = backendError;
                }
            }
            setErrors({ general: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    // Navigation handlers
    const handleNext = async (): Promise<void> => {
        if (currentSlide === 0) {
            // Welcome slide - just advance
            setCurrentSlide(1);
        } else if (currentSlide === 1) {
            // Email slide - validate email
            if (validateEmail()) {
                setCurrentSlide(2);
            }
        } else if (currentSlide === 2) {
            // Code slide - validate code and check with API
            if (validateCode()) {
                const isValid = await verifyInvitationCode();
                if (isValid) {
                    setCurrentSlide(3);
                }
            }
        } else if (currentSlide === 3) {
            // Password slide - validate and register
            if (validatePasswordForm()) {
                await handleRegister();
            }
        }
    };

    const handlePrevious = (): void => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
            setErrors({});
        }
    };

    // Can continue check (for button state)
    const canContinue = (): boolean => {
        if (loading) return false;
        if (currentSlide === 0) return true;
        if (currentSlide === 1) return formData.email.trim() !== '';
        if (currentSlide === 2) return formData.invitationCode.trim() !== '';
        if (currentSlide === 3) {
            return (
                formData.firstName.trim() !== '' &&
                formData.lastName.trim() !== '' &&
                formData.password !== '' &&
                formData.confirmPassword !== ''
            );
        }
        return false;
    };

    return (
        <div
            className="d-flex align-items-center"
            style={{ width: '100%', minHeight: '100vh' }}
        >
            {/* Previous Button - 10% width */}
            <div
                style={{
                    flex: '0 0 10%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {currentSlide > 0 && (
                    <button
                        onClick={handlePrevious}
                        disabled={loading}
                        style={{
                            fontSize: '3rem',
                            width: '80px',
                            height: '80px',
                            border: 'none',
                            background: 'none',
                            color: loading ? '#ccc' : 'var(--color-dark)',
                            cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <i className="bi bi-chevron-left"></i>
                    </button>
                )}
            </div>

            {/* Content Area - 80% width */}
            <div
                style={{
                    flex: '0 0 80%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0 2rem',
                }}
            >
                <div style={{ maxWidth: '1200px', width: '100%' }}>
                    {/* General Error Alert */}
                    {errors.general && (
                        <div
                            className="alert alert-danger text-center"
                            role="alert"
                            style={{ margin: '0 auto 2rem' }}
                        >
                            <i className="bi bi-exclamation-circle me-2"></i>
                            {errors.general}
                        </div>
                    )}

                    {/* Step 0: Welcome */}
                    {currentSlide === 0 && (
                        <div
                            style={{
                                padding: '0',
                                margin: '0 auto',
                                width: '100%',
                                textAlign: 'center',
                            }}
                        >
                            <p style={{ fontSize: '1.1rem', lineHeight: '2.2', fontWeight: '300' }}>
                                Bienvenidos a <strong>PLANO INVISIBLE</strong>, un lugar donde se unen arquitectura, psicología y neurociencia para indagar en cómo debe ser el lugar que te acogerá a partir de ahora.
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2.2', fontWeight: '300' }}>
                                En Plano invisible nos dedicamos a entender qué deseos y necesidades tienes y ver cómo percibes tú el espacio. Entender esta información esencial permitirá trazar las líneas de una casa donde te sientas representado, que potencie quién eres y cómo vives.
                            </p>
                            <p style={{ fontSize: '1.1rem', lineHeight: '2.2', fontWeight: '300' }}>
                                Para que tu casa sume a tu vida.
                            </p>
                        </div>
                    )}

                    {/* Step 1: Email */}
                    {currentSlide === 1 && (
                        <div
                            style={{
                                padding: '0',
                                maxWidth: '800px',
                                margin: '0 auto',
                                width: '100%',
                            }}
                        >
                            <p className="text-center mb-4" style={{ fontSize: '1.1rem' }}>
                                Introduce tu email
                            </p>
                            <div className="mb-3">
                                <input
                                    type="email"
                                    className="form-control text-center"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="tu@email.com"
                                    style={{
                                        backgroundColor: '#eeeeee',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '0.75rem'
                                    }}
                                    disabled={loading}
                                />
                                {errors.email && (
                                    <div className="text-danger small mt-1 text-center">{errors.email}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Invitation Code */}
                    {currentSlide === 2 && (
                        <div
                            style={{
                                padding: '0',
                                maxWidth: '800px',
                                margin: '0 auto',
                                width: '100%',
                            }}
                        >
                            <p className="text-center mb-4" style={{ fontSize: '1.1rem' }}>
                                Introduce tu código de invitación
                            </p>
                            <div className="d-flex justify-content-center gap-2 mb-3">
                                {codeDigits.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => { codeInputRefs.current[index] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        className="form-control text-center"
                                        value={digit}
                                        onChange={(e) => handleCodeDigitChange(index, e.target.value)}
                                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                        onPaste={handleCodePaste}
                                        maxLength={1}
                                        style={{
                                            backgroundColor: '#eeeeee',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            padding: '0.75rem',
                                            width: '50px',
                                            height: '60px',
                                            fontSize: '1.5rem',
                                            fontWeight: '500'
                                        }}
                                        disabled={loading}
                                    />
                                ))}
                            </div>
                            {errors.invitationCode && (
                                <div className="text-danger small mt-1 text-center">{errors.invitationCode}</div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Personal Data & Password */}
                    {currentSlide === 3 && (
                        <div
                            style={{
                                padding: '0',
                                maxWidth: '800px',
                                margin: '0 auto',
                                width: '100%',
                            }}
                        >
                            <p className="text-center mb-5" style={{ fontSize: '1.1rem' }}>
                                Completa tu registro
                            </p>

                            {/* Name fields - same row */}
                            <div className="d-flex gap-3 mb-5">
                                <div style={{ flex: '1' }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        placeholder="Nombre"
                                        style={{
                                            backgroundColor: '#eeeeee',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            padding: '0.75rem'
                                        }}
                                        disabled={loading}
                                    />
                                    {errors.firstName && (
                                        <div className="text-danger small mt-1">{errors.firstName}</div>
                                    )}
                                </div>
                                <div style={{ flex: '1' }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        placeholder="Apellidos"
                                        style={{
                                            backgroundColor: '#eeeeee',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            padding: '0.75rem'
                                        }}
                                        disabled={loading}
                                    />
                                    {errors.lastName && (
                                        <div className="text-danger small mt-1">{errors.lastName}</div>
                                    )}
                                </div>
                            </div>

                            {/* Password */}
                            <div className="mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Contraseña"
                                    style={{
                                        backgroundColor: '#eeeeee',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '0.75rem'
                                    }}
                                    disabled={loading}
                                />
                                {errors.password && (
                                    <div className="text-danger small mt-1">{errors.password}</div>
                                )}
                                <small className="text-muted d-block mt-1">
                                    Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número
                                </small>
                            </div>

                            {/* Confirm Password */}
                            <div className="mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirmar contraseña"
                                    style={{
                                        backgroundColor: '#eeeeee',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '0.75rem'
                                    }}
                                    disabled={loading}
                                />
                                {errors.confirmPassword && (
                                    <div className="text-danger small mt-1">{errors.confirmPassword}</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Next Button - 10% width */}
            <div
                style={{
                    flex: '0 0 10%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <button
                    onClick={handleNext}
                    disabled={!canContinue()}
                    style={{
                        fontSize: '3rem',
                        width: '80px',
                        height: '80px',
                        border: 'none',
                        background: 'none',
                        color: canContinue() ? 'var(--color-dark)' : '#ccc',
                        cursor: canContinue() ? 'pointer' : 'not-allowed',
                    }}
                >
                    {loading ? (
                        <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                        ></span>
                    ) : (
                        <i className="bi bi-chevron-right"></i>
                    )}
                </button>
            </div>
        </div>
    );
};

export default InviteRegistration;
